// Galaxy Galleria design tokens, read from the ONE published sheet (ASK-135 / D-072).
//
// WHY THIS EXISTS. Most chrome takes its colour through `var(--gg-*)` in CSS and needs
// nothing here. Two kinds of surface cannot:
//
//   1. things PAINTED INTO A 2D CANVAS — the compass tape, the north rose. `ctx.fillStyle`
//      takes a colour string, not a custom property.
//   2. things handed to THREE as a material/line colour that are CHROME rather than scene
//      (e.g. an editor highlight), where the value must be a number/`THREE.Color`.
//
// Both used to carry literals, which is how the amber HUD survived every previous pass: a
// grep for brand colour in CSS could not see them. They now resolve the same tokens the
// stylesheet does, so a token change moves the canvas too.
//
// 🔴 The resolver NEVER invents a fallback colour. If the sheet is missing, `token()` returns
// the empty string and the caller is expected to fail visibly rather than paint a guess — a
// silently-substituted colour is exactly the "green while the wiring is broken" shape the
// gates exist to catch. `probe-brand` asserts a resolved value, not merely that a call was made.

let _cache = null;

/** Drop the memo — call after the sheet could have changed (theme swap, late load). */
export function invalidateTokenCache() { _cache = null; }

function _styles() {
	if (_cache) return _cache;
	if (typeof document === "undefined" || !document.documentElement) return null;
	_cache = getComputedStyle(document.documentElement);
	return _cache;
}

/**
 * Resolve one `--gg-*` token to its string value.
 * @param {string} name token name, with or without the leading `--` (e.g. "gg-accent").
 * @returns {string} the trimmed value, or "" when the sheet is not loaded.
 */
export function token(name) {
	const s = _styles();
	if (!s) return "";
	const key = name.startsWith("--") ? name : `--${name}`;
	return (s.getPropertyValue(key) || "").trim();
}

/**
 * Resolve a token and apply an alpha, for canvas paints that need a wash of a token colour.
 *
 * Returns a plain `rgba()` string DELIBERATELY, rather than the `color-mix()` the stylesheet
 * uses: a 2D canvas `fillStyle` is parsed by a narrower colour parser than a stylesheet, and
 * an unparseable value is silently IGNORED — the previous fill keeps painting and nothing
 * throws. That failure is invisible on a dark HUD, so the one place we cannot afford a
 * parser surprise is exactly this one.
 *
 * @param {string} name token name — must resolve to a hex colour (the surface/text families do).
 * @param {number} alpha 0..1.
 * @returns {string} `rgba(r, g, b, a)`, or "" if the token is missing or not hex.
 */
export function tokenAlpha(name, alpha) {
	const base = token(name);
	if (!base) return "";
	const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(base);
	if (!m) return "";
	const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
	const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
	const a = Math.max(0, Math.min(1, alpha));
	return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/** True once the sheet is present — the one check a gate needs before trusting a paint. */
export function tokensLoaded() {
	return token("gg-tokens-version") !== "";
}
